/**
 * @file medicalData.ts
 * @description Datos médicos predefinidos para el contexto chileno
 * Incluye enfermedades comunes, alergias, medicamentos y tipos de sangre
 */

export interface MedicalOption {
  value: string;
  label: string;
  category?: string;
  description?: string;
}

/**
 * Tipos de sangre según sistema ABO y Rh
 */
export const bloodTypes: MedicalOption[] = [
  { value: 'A+', label: 'A Positivo (A+)' },
  { value: 'A-', label: 'A Negativo (A-)' },
  { value: 'B+', label: 'B Positivo (B+)' },
  { value: 'B-', label: 'B Negativo (B-)' },
  { value: 'AB+', label: 'AB Positivo (AB+)' },
  { value: 'AB-', label: 'AB Negativo (AB-)' },
  { value: 'O+', label: 'O Positivo (O+)' },
  { value: 'O-', label: 'O Negativo (O-)' },
  { value: 'unknown', label: 'No sé mi tipo de sangre' },
];

/**
 * Enfermedades comunes en Chile según MINSAL
 */
export const commonDiseases: MedicalOption[] = [
  // Enfermedades cardiovasculares
  { value: 'hipertension', label: 'Hipertensión arterial', category: 'cardiovascular' },
  { value: 'cardiopatia_coronaria', label: 'Cardiopatía coronaria', category: 'cardiovascular' },
  { value: 'arritmia', label: 'Arritmia cardíaca', category: 'cardiovascular' },
  { value: 'insuficiencia_cardiaca', label: 'Insuficiencia cardíaca', category: 'cardiovascular' },
  
  // Diabetes y endocrino
  { value: 'diabetes_tipo1', label: 'Diabetes tipo 1', category: 'endocrino' },
  { value: 'diabetes_tipo2', label: 'Diabetes tipo 2', category: 'endocrino' },
  { value: 'hipotiroidismo', label: 'Hipotiroidismo', category: 'endocrino' },
  { value: 'hipertiroidismo', label: 'Hipertiroidismo', category: 'endocrino' },
  
  // Respiratorias
  { value: 'asma', label: 'Asma bronquial', category: 'respiratorio' },
  { value: 'epoc', label: 'EPOC (Enfermedad Pulmonar Obstructiva Crónica)', category: 'respiratorio' },
  { value: 'bronquitis_cronica', label: 'Bronquitis crónica', category: 'respiratorio' },
  
  // Neurológicas
  { value: 'epilepsia', label: 'Epilepsia', category: 'neurologico' },
  { value: 'migrana', label: 'Migraña', category: 'neurologico' },
  { value: 'alzheimer', label: 'Alzheimer', category: 'neurologico' },
  { value: 'parkinson', label: 'Parkinson', category: 'neurologico' },
  
  // Psiquiátricas
  { value: 'depresion', label: 'Depresión', category: 'psiquiatrico' },
  { value: 'ansiedad', label: 'Trastorno de ansiedad', category: 'psiquiatrico' },
  { value: 'bipolar', label: 'Trastorno bipolar', category: 'psiquiatrico' },
  
  // Gastrointestinales
  { value: 'gastritis', label: 'Gastritis', category: 'gastrointestinal' },
  { value: 'ulcera_peptica', label: 'Úlcera péptica', category: 'gastrointestinal' },
  { value: 'colon_irritable', label: 'Síndrome de colon irritable', category: 'gastrointestinal' },
  
  // Renales
  { value: 'insuficiencia_renal', label: 'Insuficiencia renal', category: 'renal' },
  { value: 'calculos_renales', label: 'Cálculos renales', category: 'renal' },
  
  // Otros
  { value: 'artritis', label: 'Artritis', category: 'reumatologico' },
  { value: 'osteoporosis', label: 'Osteoporosis', category: 'reumatologico' },
  { value: 'cancer', label: 'Cáncer (especificar tipo)', category: 'oncologico' },
  { value: 'otro', label: 'Otra enfermedad (especificar)' },
];

/**
 * Alergias comunes en Chile
 */
export const commonAllergies: MedicalOption[] = [
  // Medicamentos
  { value: 'penicilina', label: 'Penicilina', category: 'medicamentos' },
  { value: 'aspirina', label: 'Aspirina (AAS)', category: 'medicamentos' },
  { value: 'ibuprofeno', label: 'Ibuprofeno', category: 'medicamentos' },
  { value: 'paracetamol', label: 'Paracetamol', category: 'medicamentos' },
  { value: 'sulfonamidas', label: 'Sulfonamidas', category: 'medicamentos' },
  { value: 'anestesia_local', label: 'Anestesia local', category: 'medicamentos' },
  
  // Alimentos
  { value: 'mariscos', label: 'Mariscos', category: 'alimentos' },
  { value: 'pescado', label: 'Pescado', category: 'alimentos' },
  { value: 'nueces', label: 'Nueces y frutos secos', category: 'alimentos' },
  { value: 'lacteos', label: 'Lácteos', category: 'alimentos' },
  { value: 'huevos', label: 'Huevos', category: 'alimentos' },
  { value: 'gluten', label: 'Gluten', category: 'alimentos' },
  { value: 'soja', label: 'Soja', category: 'alimentos' },
  { value: 'chocolate', label: 'Chocolate', category: 'alimentos' },
  
  // Ambientales
  { value: 'polen', label: 'Polen', category: 'ambiental' },
  { value: 'acaros', label: 'Ácaros del polvo', category: 'ambiental' },
  { value: 'pelo_animales', label: 'Pelo de animales', category: 'ambiental' },
  { value: 'latex', label: 'Látex', category: 'ambiental' },
  { value: 'picaduras_insectos', label: 'Picaduras de insectos', category: 'ambiental' },
  
  // Otros
  { value: 'yodo', label: 'Yodo (contraste radiológico)', category: 'otros' },
  { value: 'otro', label: 'Otra alergia (especificar)' },
];

/**
 * Medicamentos comunes en Chile según CENABAST
 */
export const commonMedications: MedicalOption[] = [
  // Cardiovasculares
  { value: 'enalapril', label: 'Enalapril', category: 'cardiovascular' },
  { value: 'losartan', label: 'Losartán', category: 'cardiovascular' },
  { value: 'amlodipino', label: 'Amlodipino', category: 'cardiovascular' },
  { value: 'atenolol', label: 'Atenolol', category: 'cardiovascular' },
  { value: 'simvastatina', label: 'Simvastatina', category: 'cardiovascular' },
  
  // Diabetes
  { value: 'metformina', label: 'Metformina', category: 'diabetes' },
  { value: 'glibenclamida', label: 'Glibenclamida', category: 'diabetes' },
  { value: 'insulina', label: 'Insulina', category: 'diabetes' },
  
  // Respiratorios
  { value: 'salbutamol', label: 'Salbutamol (inhalador)', category: 'respiratorio' },
  { value: 'beclometasona', label: 'Beclometasona (inhalador)', category: 'respiratorio' },
  
  // Neurológicos
  { value: 'fenitoina', label: 'Fenitoína', category: 'neurologico' },
  { value: 'carbamazepina', label: 'Carbamazepina', category: 'neurologico' },
  { value: 'levodopa', label: 'Levodopa', category: 'neurologico' },
  
  // Psiquiátricos
  { value: 'fluoxetina', label: 'Fluoxetina', category: 'psiquiatrico' },
  { value: 'sertralina', label: 'Sertralina', category: 'psiquiatrico' },
  { value: 'risperidona', label: 'Risperidona', category: 'psiquiatrico' },
  { value: 'litio', label: 'Litio', category: 'psiquiatrico' },
  
  // Analgésicos
  { value: 'paracetamol', label: 'Paracetamol', category: 'analgesico' },
  { value: 'ibuprofeno', label: 'Ibuprofeno', category: 'analgesico' },
  { value: 'diclofenaco', label: 'Diclofenaco', category: 'analgesico' },
  { value: 'tramadol', label: 'Tramadol', category: 'analgesico' },
  
  // Gastrointestinales
  { value: 'omeprazol', label: 'Omeprazol', category: 'gastrointestinal' },
  { value: 'ranitidina', label: 'Ranitidina', category: 'gastrointestinal' },
  
  // Hormonales
  { value: 'levotiroxina', label: 'Levotiroxina', category: 'hormonal' },
  { value: 'prednisona', label: 'Prednisona', category: 'hormonal' },
  
  // Anticoagulantes
  { value: 'warfarina', label: 'Warfarina', category: 'anticoagulante' },
  { value: 'aspirina_cardio', label: 'Aspirina (dosis cardioprotectora)', category: 'anticoagulante' },
  
  // Otros
  { value: 'otro', label: 'Otro medicamento (especificar)' },
];

/**
 * Categorías para organizar las opciones
 */
export const medicalCategories = {
  diseases: {
    cardiovascular: 'Cardiovasculares',
    endocrino: 'Endocrinas',
    respiratorio: 'Respiratorias',
    neurologico: 'Neurológicas',
    psiquiatrico: 'Psiquiátricas',
    gastrointestinal: 'Gastrointestinales',
    renal: 'Renales',
    reumatologico: 'Reumatológicas',
    oncologico: 'Oncológicas',
  },
  allergies: {
    medicamentos: 'Medicamentos',
    alimentos: 'Alimentos',
    ambiental: 'Ambientales',
    otros: 'Otros',
  },
  medications: {
    cardiovascular: 'Cardiovasculares',
    diabetes: 'Diabetes',
    respiratorio: 'Respiratorios',
    neurologico: 'Neurológicos',
    psiquiatrico: 'Psiquiátricos',
    analgesico: 'Analgésicos',
    gastrointestinal: 'Gastrointestinales',
    hormonal: 'Hormonales',
    anticoagulante: 'Anticoagulantes',
  },
};

/**
 * Función para obtener opciones por categoría
 */
export const getOptionsByCategory = (options: MedicalOption[], category: string) => {
  return options.filter(option => option.category === category);
};

/**
 * Función para buscar opciones
 */
export const searchOptions = (options: MedicalOption[], searchTerm: string) => {
  const term = searchTerm.toLowerCase();
  return options.filter(option => 
    option.label.toLowerCase().includes(term) ||
    option.value.toLowerCase().includes(term)
  );
};
